import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { styled } from '@mui/material/styles';
import { Box, TextField, Card, Stack, Typography, IconButton, InputAdornment } from '@mui/material';
import { inputBaseClasses } from '@mui/material/InputBase';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';

import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { MAPBOX_API_KEY } from 'src/config-global';

// Include Mapbox CSS
import 'mapbox-gl/dist/mapbox-gl.css';

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_API_KEY || '';

const MapContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '700px',
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  '& .mapboxgl-ctrl-attrib': {
    fontSize: '10px',
  },
  '& .mapboxgl-ctrl-attrib a': {
    color: theme.palette.text.secondary,
  },
  '& .mapboxgl-ctrl-bottom-right': {
    display: 'none', // Hide attribution on small screens
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  [theme.breakpoints.down('md')]: {
    height: '550px',
    borderRadius: theme.spacing(1),
  },
}));

const SearchContainer = styled(Card)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  left: theme.spacing(1),
  width: '40%', // 40% of map width
  zIndex: 1000,
  padding: theme.spacing(1),
  boxShadow: theme.shadows[16],
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.up('md')]: {
    top: theme.spacing(2),
    left: theme.spacing(2),
    width: '40%', // 40% of map width on desktop too
    padding: theme.spacing(1.5),
  },
}));

const SuggestionsList = styled(Box)(({ theme }) => ({
  maxHeight: '120px', // Reduced height to not cover map
  overflowY: 'auto',
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[8],
  [theme.breakpoints.up('md')]: {
    maxHeight: '160px',
  },
}));

const SuggestionItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  cursor: 'pointer',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(1.5),
  },
}));

interface Coordinates {
  lat: number;
  lng: number;
}

interface LocationPickerProps {
  initialCoordinates?: Coordinates;
  initialAddress?: string;
  onLocationChange?: (coordinates: Coordinates, address?: string) => void;
  placeholder?: string;
  height?: string | number;
}

interface Suggestion {
  id: string;
  place_name: string;
  center: [number, number];
}

export default function MapboxLocationPicker({
  initialCoordinates,
  initialAddress = '',
  onLocationChange,
  placeholder,
  height = 700,
}: LocationPickerProps) {
  const { t } = useLocales();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);

  const [searchQuery, setSearchQuery] = useState(initialAddress);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [mapError, setMapError] = useState<string | null>(null);
  const [selectedCoordinates, setSelectedCoordinates] = useState<Coordinates | null>(
    initialCoordinates || null,
  );

  // Reverse geocoding
  const reverseGeocode = useCallback(
    async (lng: number, lat: number) => {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_API_KEY}`,
        );
        const data = await response.json();

        if (data.features && data.features.length > 0) {
          const address = data.features[0].place_name;
          setSearchQuery(address);

          if (onLocationChange) {
            onLocationChange({ lat, lng }, address);
          }
        }
      } catch (error) {
        console.error('Reverse geocoding error:', error);
      }
    },
    [onLocationChange],
  );

  // Add marker to map
  const addMarker = useCallback(
    (lng: number, lat: number) => {
      if (!map.current) return;

      // Remove existing marker
      if (marker.current) {
        marker.current.remove();
      }

      // Create new draggable marker
      marker.current = new mapboxgl.Marker({
        draggable: true,
      })
        .setLngLat([lng, lat])
        .addTo(map.current);

      // Handle marker drag
      marker.current.on('dragend', () => {
        if (!marker.current) return;

        const lngLat = marker.current.getLngLat();
        setSelectedCoordinates({ lat: lngLat.lat, lng: lngLat.lng });

        // Reverse geocoding to get address
        reverseGeocode(lngLat.lng, lngLat.lat);

        if (onLocationChange) {
          onLocationChange({ lat: lngLat.lat, lng: lngLat.lng });
        }
      });
    },
    [onLocationChange, reverseGeocode],
  );

  // Handle map click
  const handleMapClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      const { lng, lat } = e.lngLat;
      addMarker(lng, lat);
      setSelectedCoordinates({ lat, lng });

      // Reverse geocoding to get address
      reverseGeocode(lng, lat);

      if (onLocationChange) {
        onLocationChange({ lat, lng });
      }
    },
    [onLocationChange, addMarker, reverseGeocode],
  );

  // Search for places
  const searchPlaces = useCallback(async (query: string) => {
    if (!query.trim() || !MAPBOX_API_KEY) {
      return;
    }

    try {
      // Improved URL with better parameters for European cities
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_API_KEY}&limit=8&types=place,locality,neighborhood,address&country=IT,FR,ES,DE,GB,NL,BE,AT,CH,PT,GR,IE,PL,CZ,HU,RO,BG,HR,SI,SK,LT,LV,EE,LU,MT,CY,FI,SE,DK,NO&autocomplete=true`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const searchResults = data.features.map((feature: any) => ({
          id: feature.id,
          place_name: feature.place_name,
          center: feature.center,
        }));
        setSuggestions(searchResults);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setSuggestions([]);
    }
  }, []);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback(
    (suggestion: Suggestion) => {
      const [lng, lat] = suggestion.center;

      setSearchQuery(suggestion.place_name);
      setSuggestions([]);
      setSelectedCoordinates({ lat, lng });

      // Fly to location
      if (map.current) {
        map.current.flyTo({
          center: [lng, lat],
          zoom: 15,
          essential: true,
        });
      }

      // Add marker
      addMarker(lng, lat);

      if (onLocationChange) {
        onLocationChange({ lat, lng }, suggestion.place_name);
      }
    },
    [addMarker, onLocationChange],
  );

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchQuery(value);
  }, []);

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setSuggestions([]);
  }, []);

  // Handle search input focus (clear suggestions when clicking outside)
  const handleSearchBlur = useCallback(() => {
    // Delay clearing suggestions to allow for selection
    setTimeout(() => {
      setSuggestions([]);
    }, 300); // Increased delay to 300ms
  }, []);

  // Handle search input focus
  const handleSearchFocus = useCallback(() => {
    // Don't clear suggestions when focusing
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length > 2) {
        searchPlaces(searchQuery);
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchPlaces]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current || !MAPBOX_API_KEY) return;

    try {
      // Italy center coordinates (Rome area)
      const italyCenter: [number, number] = [12.4964, 41.9028];
      const initialCenter: [number, number] = initialCoordinates
        ? [initialCoordinates.lng, initialCoordinates.lat]
        : italyCenter; // Default to Italy center

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: initialCenter,
        zoom: initialCoordinates ? 13 : 6, // Zoom level 6 shows Italy nicely
        // Better mobile support
        touchZoomRotate: true,
        touchPitch: false,
        dragRotate: false,
        pitchWithRotate: false,
      });

      // Add navigation control
      map.current.addControl(
        new mapboxgl.NavigationControl({
          showCompass: false, // Hide compass on mobile for cleaner UI
        }),
        'top-right',
      );

      // Add click handler for map
      map.current.on('click', handleMapClick);

      // Handle map errors
      map.current.on('error', (e) => {
        console.error('Mapbox error:', e);
        setMapError('Failed to load map. Please check your internet connection.');
      });

      // Add initial marker if coordinates are provided
      if (initialCoordinates) {
        addMarker(initialCoordinates.lng, initialCoordinates.lat);
      }
    } catch (error) {
      console.error('Error initializing map:', error);
      setMapError('Failed to initialize map.');
    }

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [initialCoordinates, handleMapClick, addMarker]);

  // Update search query when initialAddress changes
  useEffect(() => {
    if (initialAddress && initialAddress.trim() !== '') {
      setSearchQuery(initialAddress);
    }
  }, [initialAddress]);

  // Update coordinates when initialCoordinates changes
  useEffect(() => {
    if (initialCoordinates) {
      setSelectedCoordinates(initialCoordinates);
    }
  }, [initialCoordinates]);

  // Check if Mapbox API key is available - render fallback
  if (!MAPBOX_API_KEY) {
    return (
      <Box
        sx={{
          height: typeof height === 'number' ? `${height}px` : height,
          border: '2px dashed',
          borderColor: 'error.main',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          p: 3,
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Mapbox Configuration Required
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          Please configure VITE_MAPBOX_API_KEY in your environment variables to enable map
          functionality.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <MapContainer
          ref={mapContainer}
          sx={{ height: typeof height === 'number' ? `${height}px` : height }}
        >
          {mapError && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.8)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                flexDirection: 'column',
                p: 2,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Map Error
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                }}
              >
                {mapError}
              </Typography>
            </Box>
          )}

          <SearchContainer>
            <TextField
              fullWidth
              size="small"
              placeholder={placeholder || t('common.search_for_address')}
              value={searchQuery}
              onChange={handleSearchChange}
              onBlur={handleSearchBlur}
              onFocus={handleSearchFocus}
              variant="outlined"
              sx={{
                width: '100%',
                minWidth: '100%',
                [`& .${outlinedInputClasses.root}`]: {
                  backgroundColor: 'background.paper',
                  width: '100%',
                },
                [`& .${inputBaseClasses.input}`]: {
                  width: '100%',
                },
              }}
              slotProps={{
                input: {
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClearSearch}
                        onMouseDown={(e) => e.preventDefault()} // Prevent blur
                        size="small"
                        edge="end"
                      >
                        <Iconify icon="mingcute:close-line" width={16} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            {suggestions.length > 0 && (
              <SuggestionsList>
                {suggestions.map((suggestion, index) => (
                  <SuggestionItem
                    key={`${suggestion.id}-${index}`}
                    onClick={() => handleSuggestionSelect(suggestion)}
                    onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking
                  >
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {suggestion.place_name}
                    </Typography>
                  </SuggestionItem>
                ))}
              </SuggestionsList>
            )}
          </SearchContainer>
        </MapContainer>
      </Box>

      {selectedCoordinates && (
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Selected coordinates: {selectedCoordinates.lat.toFixed(6)},{' '}
            {selectedCoordinates.lng.toFixed(6)}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
